import React, { useState, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import * as S from './AdvancesStyles';

const CATS = ['Sugar', 'Medicine', 'Labour', 'Transport', 'Maintenance', 'Other'];

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');

const fmtDate = d => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const LedgerTab = ({
  txns,
  members,
  apiaries,
  projects,
  member,
  apiary,
  project,
  locLabel,
  isAdmin = true,
  onDecide,
  onViewDetails,
  onMarkSinglePaid,
  onOpenPassbook
}) => {
  const [fType, setFType] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fMember, setFMember] = useState('');
  const [fMode, setFMode] = useState('');
  const [fCat, setFCat] = useState('');
  const [fLoc, setFLoc] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');

  const clearFilters = () => {
    setFType('');
    setFStatus('');
    setFMember('');
    setFMode('');
    setFCat('');
    setFLoc('');
    setFFrom('');
    setFTo('');
  };

  const filteredTxns = useMemo(() => {
    let rows = [...txns].sort((a, b) => b.date.localeCompare(a.date));
    return rows.filter(t => {
      if (fType && t.type !== fType) return false;
      if (fStatus && (t.type !== 'expense' || t.approvalStatus !== fStatus))
        return false;
      if (fMember && t.memberId !== fMember) return false;
      const m = member(t.memberId);
      if (fMode && m && m.mode !== fMode) return false;
      if (fCat && t.category !== fCat) return false;
      if (fLoc) {
        const [kind, val] = fLoc.split(':');
        if (
          kind === 'apiary' &&
          !(t.tagType === 'apiary' && t.locId === val) &&
          !(t.type === 'advance_issued' && t.locId === val)
        )
          return false;
        if (
          kind === 'project' &&
          !(t.tagType === 'project_overhead' && t.locId === val)
        )
          return false;
      }
      if (fFrom && t.date < fFrom) return false;
      if (fTo && t.date > fTo) return false;
      return true;
    });
  }, [txns, fType, fStatus, fMember, fMode, fCat, fLoc, fFrom, fTo, member]);

  const typePill = t => {
    if (t.type === 'advance_issued')
      return <S.Pill variant="advance">Advance Issued</S.Pill>;
    if (t.type === 'advance_settled')
      return <S.Pill variant="settled">Advance Settled</S.Pill>;
    if (t.type === 'adjustment')
      return <S.Pill variant="settled">Adjustment</S.Pill>;
    return <S.Pill variant="expense">Expense</S.Pill>;
  };

  const statusPill = t => {
    if (t.type !== 'expense') return <span className="rowmuted">—</span>;
    const s = t.approvalStatus || 'pending';
    const approver = t.approved_by_name || (typeof t.approved_by === 'object' ? (t.approved_by?.name || t.approved_by?.username) : t.approved_by);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <S.Pill variant={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</S.Pill>
        {s !== 'pending' && approver && (
          <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>
            By {approver}
          </span>
        )}
      </div>
    );
  };

  const payoutPill = t => {
    if (
      t.type !== 'expense' ||
      t.fundingSource !== 'reimbursement' ||
      t.approvalStatus !== 'approved'
    )
      return <span className="rowmuted">—</span>;
    return <S.Pill variant={t.payoutStatus}>{t.payoutStatus}</S.Pill>;
  };

  const columns = [
    {
      dataField: 'date',
      text: 'Date',
      sort: true,
      formatter: (cell) => fmtDate(cell)
    },
    {
      dataField: 'memberId',
      text: 'Member',
      formatter: (cell, t) => {
        const m = member(t.memberId);
        return (
          <span className="member-link" onClick={() => onOpenPassbook(m?.id || t.memberId)}>
            {m?.name || t.memberName || 'Staff'}
          </span>
        );
      }
    },
    {
      dataField: 'employee_id_col',
      text: 'Employee ID',
      formatter: (cell, t) => {
        const m = member(t.memberId);
        const empId = t.user_employee_id || t.employee_id || t.paid_by_employee_id || t.created_by_employee_id || m?.employee_id;
        if (!empId) return <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>;
        return (
          <span style={{
            fontWeight: 700,
            fontSize: '11px',
            fontFamily: 'monospace',
            color: '#2563eb',
            background: '#eff6ff',
            padding: '3px 8px',
            borderRadius: '4px',
            border: '1px solid #bfdbfe'
          }}>
            {empId}
          </span>
        );
      }
    },
    {
      dataField: 'reportingToName',
      text: 'Reporting To',
      formatter: (cell, t) =>
        t.type === 'expense' && t.reportingToName ? (
          <span
            style={{
              background: '#f8fafc',
              color: '#334155',
              padding: '3px 8px',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '12px',
              border: '1px solid #e2e8f0',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <i className="fa fa-user-circle-o" style={{ color: '#64748b' }}></i>
            {t.reportingToName}
          </span>
        ) : (
          <span className="rowmuted">—</span>
        )
    },
    {
      dataField: 'type',
      text: 'Type',
      formatter: (cell, t) => typePill(t)
    },
    {
      dataField: 'amount',
      text: 'Amount',
      sort: true,
      formatter: (cell) => <strong>{fmt(cell)}</strong>
    },
    {
      dataField: 'locId',
      text: 'Tagged To',
      formatter: (cell, t) =>
        t.type === 'adjustment' || t.type === 'advance_settled' ? (
          <span className="rowmuted">—</span>
        ) : t.type === 'advance_issued' ? (
          t.locId ? (
            <S.Chip blue>{apiary(t.locId)?.name}</S.Chip>
          ) : (
            <span className="rowmuted">Independent</span>
          )
        ) : (
          <S.Chip blue>{locLabel(t)}</S.Chip>
        )
    },
    {
      dataField: 'category',
      text: 'Category',
      formatter: (cell, t) =>
        t.type === 'adjustment' ? (
          <span className="rowmuted">{t.comment || 'Reconciliation'}</span>
        ) : t.category ? (
          <S.Chip>{t.category}</S.Chip>
        ) : (
          <span className="rowmuted">—</span>
        )
    },
    {
      dataField: 'approvalStatus',
      text: 'Approval',
      formatter: (cell, t) => statusPill(t)
    },
    {
      dataField: 'payoutStatus',
      text: 'Payout',
      formatter: (cell, t) => payoutPill(t)
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      formatter: (cell, t) => (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {t.type === 'expense' && (
            <S.ActionIconBtn
              onClick={() => onViewDetails(t)}
              title="View Details & Proofs"
              style={{
                border: '1px solid #bae6fd',
                background: '#f0f9ff',
                color: '#0284c7'
              }}
            >
              <i className="fa fa-eye"></i>
            </S.ActionIconBtn>
          )}

          {isAdmin && t.type === 'expense' && t.approvalStatus === 'pending' && (
            <>
              <S.ActionIconBtn
                variant="approve"
                onClick={() => onDecide(t.id, 'approved')}
                title="Approve"
              >
                ✓
              </S.ActionIconBtn>
              <S.ActionIconBtn
                variant="reject"
                onClick={() => onDecide(t.id, 'rejected')}
                title="Reject"
              >
                ✕
              </S.ActionIconBtn>
            </>
          )}

          {isAdmin &&
            t.type === 'expense' &&
            t.fundingSource === 'reimbursement' &&
            t.approvalStatus === 'approved' &&
            t.payoutStatus === 'unpaid' && (
              <S.Btn ghost sm onClick={() => onMarkSinglePaid(t.id)}>
                Mark Paid
              </S.Btn>
            )}

          {t.type !== 'expense' && <span className="rowmuted">—</span>}
        </div>
      )
    }
  ];

  return (
    <div>
      <S.FiltersBar>
        <div className="field">
          <label>Type</label>
          <select value={fType} onChange={e => setFType(e.target.value)}>
            <option value="">All</option>
            <option value="advance_issued">Advance issued</option>
            <option value="expense">Expense</option>
            <option value="advance_settled">Advance settled</option>
          </select>
        </div>
        <div className="field">
          <label>Approval Status</label>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="field">
          <label>Member</label>
          <select value={fMember} onChange={e => setFMember(e.target.value)}>
            <option value="">All People</option>
            {members.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Mode</label>
          <select value={fMode} onChange={e => setFMode(e.target.value)}>
            <option value="">All</option>
            <option value="advance">Advance</option>
            <option value="reimbursement">Reimbursement</option>
          </select>
        </div>
        <div className="field">
          <label>Category</label>
          <select value={fCat} onChange={e => setFCat(e.target.value)}>
            <option value="">All Categories</option>
            {CATS.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Apiary / Project</label>
          <select value={fLoc} onChange={e => setFLoc(e.target.value)}>
            <option value="">All</option>
            <optgroup label="Apiaries">
              {apiaries.map(a => (
                <option key={a.id} value={`apiary:${a.id}`}>
                  {a.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Projects (overhead)">
              {projects.map(p => (
                <option key={p.id} value={`project:${p.id}`}>
                  {p.name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="field">
          <label>From</label>
          <input
            type="date"
            value={fFrom}
            onChange={e => setFFrom(e.target.value)}
          />
        </div>
        <div className="field">
          <label>To</label>
          <input
            type="date"
            value={fTo}
            onChange={e => setFTo(e.target.value)}
          />
        </div>
        <S.Btn ghost sm onClick={clearFilters}>
          Clear filters
        </S.Btn>
      </S.FiltersBar>

      <S.TableWrap>
        <BootstrapTable
          keyField="id"
          data={filteredTxns}
          columns={columns}
          bordered={false}
          hover
          noDataIndication={() => (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              No transactions match these filters.
            </div>
          )}
          pagination={paginationFactory({
            sizePerPage: 500,
            sizePerPageList: [
              { text: '10', value: 10 },
              { text: '50', value: 50 },
              { text: '100', value: 100 },
              { text: '200', value: 200 },
              { text: '500', value: 500 },
              { text: '1000', value: 1000 },
              { text: '2000', value: 2000 }
            ],
            showTotal: true
          })}
        />
      </S.TableWrap>
    </div>
  );
};

export default LedgerTab;
